/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @description
 * 사용자 처리에 관한 모든 API
 **/

import { atom } from "jotai";
import { BaseService } from "@/frameworks/libraries/restservice";

class RelatedUsers extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/related_users${path ? `/${path}` : ""}`;

	private static Cache = {
		List: atom<WebServices.RelatedUsers.List.Response>({}),
		ListGroup: atom<WebServices.RelatedUsers.ListGroup.Response>({})
	};

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.RelatedUsers.List.Request) => this.get(this.APIPath(), parameter),
		cache: RelatedUsers.Cache.List
	});

	public listGroup = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId }: WebServices.RelatedUsers.ListGroup.Request) => this.get(this.APIPath(groupId)),
		cache: RelatedUsers.Cache.ListGroup
	});
}

class User extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/users${path ? `/${path}` : ""}`;

	private static Cache = {
		entrySpace: {
			Inquiry: atom<WebServices.User.EntrySpace.Inquiry.Response>({}),
			Update: atom<WebServices.User.EntrySpace.Update.Response>({})
		},
		InquiryUsers: atom<WebServices.User.InquiryUsers.Response>({}),
		InquiryUserDetail: atom<WebServices.User.InquiryUserDetail.Response>({}),
		me: {
			Inquiry: atom<WebServices.User.Me.Inquiry.Response>({}),
			Update: atom<WebServices.User.Me.Update.Response>({})
		},
		participatedSpace: atom<WebServices.User.GetParticipatedSpace.Response>({}),
		InvitesList: atom<WebServices.User.InvitesList.Response>({}),
		Avatar: {
			Current: atom<WebServices.User.Avatar.Current.Response>({}),
			Update: atom<WebServices.User.Avatar.Update.Response>({}),
			List: atom<WebServices.User.Avatar.Get.Response>({}),
			Save: atom<WebServices.User.Avatar.Save.Response>({}),
			Delete: atom<WebServices.User.Avatar.Delete.Response>({}),
			Apply: atom<WebServices.User.Avatar.Apply.Response>({})
		},
		connect: {
			Inquiry: atom<WebServices.User.GetConnectedUser.Response>({}),
			Update: atom<WebServices.User.Connect.Response>({}),
			Delete: atom<WebServices.User.DisconnectUser.Response>({})
		},
		checkBeforeFollow: atom<WebServices.User.CheckBeforeFollow.Response>({}),
		checkBeforeSummon: atom<WebServices.User.CheckBeforeFollowMe.Response>({}),
		updateMarketingAgreement: atom<WebServices.User.UpdateMarketingAgreement.Response>({}),
		withdrawUser: atom<WebServices.User.WithdrawUser.Response>({}),
		notificationLogs: {
			Inquiry: atom<WebServices.User.Notification.Inquiry.Response>({}),
			Read: atom<WebServices.User.Notification.Read.Response>({}),
			ReadAll: atom<WebServices.User.Notification.ReadAll.Response>({}),
			ReadByKey: atom<WebServices.User.Notification.ReadByKey.Response>({})
		}
	};

	public checkBeforeFollow = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ userId }: WebServices.User.CheckBeforeFollow.Request) =>
			this.get(this.APIPath(`${userId}/follow_check`)),
		cache: User.Cache.checkBeforeFollow
	});

	public checkBeforeSummon = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ userId }: WebServices.User.CheckBeforeFollowMe.Request) =>
			this.get(this.APIPath(`${userId}/follow_me_check`)),
		cache: User.Cache.checkBeforeSummon
	});

	public entrySpace = {
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.EntrySpace.Inquiry.Request) =>
				this.get(this.APIPath("entry_space"), parameter),
			cache: User.Cache.entrySpace.Inquiry
		}),
		update: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.EntrySpace.Update.Request) =>
				this.post(this.APIPath("entry_space"), parameter),
			cache: User.Cache.entrySpace.Update
		})
	};

	public inquiryUsers = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.User.InquiryUsers.Request) => this.get(this.APIPath(), parameter),
		cache: User.Cache.InquiryUsers
	});

	public inquiryUserDetail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ userId }: WebServices.User.InquiryUserDetail.Request) => this.get(this.APIPath(userId)),
		cache: User.Cache.InquiryUserDetail
	});

	public me = {
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Me.Inquiry.Request) => this.get(this.APIPath("me"), parameter),
			cache: User.Cache.me.Inquiry
		}),
		update: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Me.Update.Request) => {
				return this.post(this.APIPath(), parameter, {
					headers: { "Content-Type": "multipart/form-data" }
				});
			},
			cache: User.Cache.me.Update
		})
	};

	public invitesList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.User.InvitesList.Request) => this.get(this.APIPath("invites"), parameter),
		cache: User.Cache.InvitesList
	});

	public avatar = {
		current: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Avatar.Current.Request) =>
				this.get(this.APIPath(`avatars/apply`), parameter),
			cache: User.Cache.Avatar.Current
		}),
		update: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Avatar.Update.Request) =>
				this.post(this.APIPath(`avatars/${parameter.id}`), parameter),
			cache: User.Cache.Avatar.Update
		}),
		list: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Avatar.Get.Request) => this.get(this.APIPath("avatars"), parameter),
			cache: User.Cache.Avatar.List
		}),
		save: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Avatar.Save.Request) =>
				this.post(this.APIPath("avatars"), parameter),
			cache: User.Cache.Avatar.Save
		}),
		delete: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ id }: WebServices.User.Avatar.Delete.Request) => this.delete(this.APIPath(`avatars/${id}`)),
			cache: User.Cache.Avatar.Delete
		}),
		apply: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Avatar.Apply.Request) =>
				this.get(this.APIPath(`avatars/apply`), parameter),
			cache: User.Cache.Avatar.Apply
		})
	};

	public participatedSpace = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.User.GetParticipatedSpace.Request) =>
			this.get(this.APIPath("participated_space"), parameter),
		cache: User.Cache.participatedSpace
	});

	public connect = {
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.GetConnectedUser.Request) =>
				this.get(this.APIPath("connect"), parameter),
			cache: User.Cache.connect.Inquiry
		}),
		update: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ connectType, ...parameter }: WebServices.User.Connect.Request) =>
				this.post(this.APIPath(`connect/${connectType}`), parameter),
			cache: User.Cache.connect.Update
		}),
		delete: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ connectType, ...parameter }: WebServices.User.DisconnectUser.Request) =>
				this.delete(this.APIPath(`connect/${connectType}`), parameter),
			cache: User.Cache.connect.Delete
		})
	};

	public updateMarketingAgreement = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.User.UpdateMarketingAgreement.Request) =>
			this.post(this.APIPath("marketing_agreement"), parameter),
		cache: User.Cache.updateMarketingAgreement
	});

	public withdrawUser = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.User.WithdrawUser.Request) => this.post(this.APIPath("delete"), parameter),
		cache: User.Cache.withdrawUser
	});

	public notificationLogs = {
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Notification.Inquiry.Request) =>
				this.get(this.APIPath("notification_logs"), parameter),
			cache: User.Cache.notificationLogs.Inquiry
		}),
		read: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ userNotificationLogId }: WebServices.User.Notification.Read.Request) =>
				this.post(this.APIPath(`notification_logs/${userNotificationLogId}/read`)),
			cache: User.Cache.notificationLogs.Read
		}),
		readAll: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Notification.ReadAll.Request) =>
				this.post(this.APIPath("notification_logs/read_all"), parameter),
			cache: User.Cache.notificationLogs.ReadAll
		}),
		readByKey: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.User.Notification.ReadByKey.Request) =>
				this.post(this.APIPath(`notification_logs/read`), parameter),
			cache: User.Cache.notificationLogs.ReadByKey
		})
	};
}

class UserOption extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/users/options/${path ? `${path}` : ""}`;

	private static Cache = {
		graphics: {
			Inquiry: atom<WebServices.UserOption.Graphics.Inquiry.Response>({}),
			Update: atom<WebServices.UserOption.Graphics.Update.Response>({})
		},
		notification: {
			Inquiry: atom<WebServices.UserOption.GetNotificationOption.Response>({}),
			Update: atom<WebServices.UserOption.UpdateNotificationOption.Response>({})
		},
		groupCreateGuide: {
			Update: atom<WebServices.UserOption.UpdateGroupCreateGuide.Response>({})
		},
		language: {
			Inquiry: atom<WebServices.UserOption.GetUserLanguage.Response>({}),
			Update: atom<WebServices.UserOption.UpdateUserLanguage.Response>({})
		},
		account: {
			Inquiry: atom<WebServices.UserOption.GetAccountOption.Response>({}),
			Update: atom<WebServices.UserOption.UpdateAccountOption.Response>({})
		}
	};

	public graphics = {
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.UserOption.Graphics.Inquiry.Request) =>
				this.get(this.APIPath("graphics"), parameter),
			cache: UserOption.Cache.graphics.Inquiry
		}),
		update: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.UserOption.Graphics.Update.Request) =>
				this.put(this.APIPath("graphics"), parameter),
			cache: UserOption.Cache.graphics.Update
		})
	};

	public notification = {
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.UserOption.GetNotificationOption.Request) =>
				this.get(this.APIPath("notification"), parameter),
			cache: UserOption.Cache.notification.Inquiry
		}),
		update: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.UserOption.GetNotificationOption.Request) =>
				this.put(this.APIPath("notification"), parameter),
			cache: UserOption.Cache.notification.Update
		})
	};

	public groupCreateGuide = {
		update: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.UserOption.UpdateGroupCreateGuide.Request) =>
				this.put(this.APIPath("group_create_guide"), parameter),
			cache: UserOption.Cache.groupCreateGuide.Update
		})
	};
	public language = {
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.UserOption.GetUserLanguage.Request) =>
				this.get(this.APIPath("languages"), parameter),
			cache: UserOption.Cache.language.Inquiry
		}),
		update: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.UserOption.UpdateUserLanguage.Request) =>
				this.put(this.APIPath("languages"), parameter),
			cache: UserOption.Cache.language.Update
		})
	};

	public account = {
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.UserOption.GetAccountOption.Request) =>
				this.get(this.APIPath("account"), parameter),
			cache: UserOption.Cache.account.Inquiry
		}),
		update: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.UserOption.UpdateAccountOption.Request) =>
				this.put(this.APIPath("account"), parameter),
			cache: UserOption.Cache.account.Update
		})
	};
}

class UserStatus extends BaseService {
	constructor() {
		super();
	}

	private APIPath = "/api/users/status";
	private APIPath2 = "/api/users/default_status";

	private static Cache = {
		Delete: atom<WebServices.UserStatus.Delete.Response>({}),
		Inquiry: atom<WebServices.UserStatus.Inquiry.Response>({}),
		Update: atom<WebServices.UserStatus.Update.Response>({}),
		DefaultInquiry: atom<WebServices.UserStatus.DefaultInquiry.Response>({})
	};

	public deleteStatus = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserStatus.Delete.Request) => this.delete(this.APIPath, parameter),
		cache: UserStatus.Cache.Delete
	});

	public inquiry = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserStatus.Inquiry.Request) => this.get(this.APIPath, parameter),
		cache: UserStatus.Cache.Inquiry
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserStatus.Update.Request) => this.post(this.APIPath, parameter),
		cache: UserStatus.Cache.Update
	});
	public defaultInquiry = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserStatus.DefaultInquiry.Request) => this.get(this.APIPath2, parameter),
		cache: UserStatus.Cache.DefaultInquiry
	});
}

class UserActiveStatus extends BaseService {
	constructor() {
		super();
	}

	private APIPath = "/api/users/active_status";

	private static Cache = {
		Update: atom<WebServices.UserActiveStatus.Update.Response>({}),
		SystemActive: atom<WebServices.UserActiveStatus.Active.Response>({}),
		SystemAway: atom<WebServices.UserActiveStatus.Away.Response>({})
	};
	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserActiveStatus.Update.Request) => this.post(this.APIPath, parameter),
		cache: UserActiveStatus.Cache.Update
	});
	public system_active = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserActiveStatus.Active.Request) =>
			this.post(this.APIPath + "/active", parameter),
		cache: UserActiveStatus.Cache.SystemActive
	});
	public system_away = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserActiveStatus.Away.Request) =>
			this.post(this.APIPath + "/away", parameter),
		cache: UserActiveStatus.Cache.SystemAway
	});
}

class UserSound extends BaseService {
	constructor() {
		super();
	}

	private APIPath = "/api/sound_notifications";

	private static Cache = {
		Inquiry: atom<WebServices.UserSound.GetSoundNotifications.Response>({})
	};

	public inquiry = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserSound.GetSoundNotifications.Request) => this.get(this.APIPath, parameter),
		cache: UserSound.Cache.Inquiry
	});
}

class UserServiceStatic {
	constructor(
		public readonly relatedUsers = new RelatedUsers(),
		public readonly user = new User(),
		public readonly userOption = new UserOption(),
		public readonly userStatus = new UserStatus(),
		public readonly userActiveStatus = new UserActiveStatus(),
		public readonly userSound = new UserSound()
	) {}
}
const UserService = new UserServiceStatic();
export default UserService;
